import {useCallback} from 'react'
import {AnchorHTMLAttributes, MouseEvent} from 'react'

import {useIsEmbeddedWithBrand} from '../../brand/hooks/useIsEmbeddedWithBrand'
import {postMessageToParent} from './postMessageToParent'

export interface UseEmbeddedLinkHandlerReturn {
  /**
   * onClick handler that handles embedded scenarios
   */
  onClick: (event: MouseEvent<HTMLElement>) => void
  /**
   * target attribute value (set to '_self' when embedded if not explicitly provided, otherwise uses provided target)
   */
  target: AnchorHTMLAttributes<HTMLAnchorElement>['target']
}

export interface UseEmbeddedLinkHandlerFactoryOptions {
  /**
   * Custom message type for postMessage (default: 'redirect')
   * Applied to all links created by this factory
   */
  messageType?: string
  /**
   * Additional data to include in the postMessage
   * Applied to all links created by this factory
   */
  additionalMessageData?: Record<string, unknown>
}

export interface UseEmbeddedLinkHandlerFactoryLinkOptions {
  /**
   * The href URL for the link
   */
  href: string
  /**
   * Optional existing onClick handler that will be called before the embedded handler
   */
  onClick?: (event: MouseEvent<HTMLElement>) => void
  /**
   * Optional target attribute override
   */
  target?: AnchorHTMLAttributes<HTMLAnchorElement>['target']
}

/**
 * Hook that returns a factory function for creating embedded link handlers.
 * This is useful when you have multiple links and want to avoid calling the hook
 * multiple times (which violates React's rules of hooks).
 *
 * Call this hook once at the component level, then use the returned factory
 * function for each link in your list.
 * @param defaultOptions - Optional default options that apply to all links created by this factory
 * @returns Factory function that creates link handlers for individual links
 * @example
 * ```tsx
 * // In component with list of links:
 * const createLinkHandler = useEmbeddedLinkHandlerFactory({
 *   messageType: 'navigate' // optional defaults for all links
 * })
 *
 * // In map:
 * {links.map(link => {
 *   const {onClick, target} = createLinkHandler({
 *     href: link.href,
 *     onClick: link.onClick // optional per-link handler
 *   })
 *   return <Link href={link.href} onClick={onClick} target={target}>...</Link>
 * })}
 * ```
 * @example
 * ```tsx
 * // With Atlas Link in a list:
 * const createLinkHandler = useEmbeddedLinkHandlerFactory()
 * {menuItems.map(item => {
 *   const {onClick, target} = createLinkHandler({href: item.url})
 *   return <Link href={item.url} onClick={onClick} target={target}>{item.label}</Link>
 * })}
 * ```
 * @example
 * ```tsx
 * // With regular anchor tags:
 * const createLinkHandler = useEmbeddedLinkHandlerFactory({
 *   messageType: 'custom-navigate',
 *   additionalMessageData: {source: 'footer'}
 * })
 * {footerLinks.map(link => {
 *   const {onClick, target} = createLinkHandler({href: link.href})
 *   return <a href={link.href} onClick={onClick} target={target}>{link.label}</a>
 * })}
 * ```
 */
export const useEmbeddedLinkHandlerFactory = () => {
  const isEmbedded = useIsEmbeddedWithBrand()

  const createHandler = useCallback(
    (
      linkOptions: UseEmbeddedLinkHandlerFactoryLinkOptions
    ): UseEmbeddedLinkHandlerReturn => {
      const {href, onClick: existingOnClick, target} = linkOptions

      const handleClick = (event: MouseEvent<HTMLElement>) => {
        // Call existing onClick handler first if provided
        existingOnClick?.(event)

        // If embedded and original target is '_blank', post message to parent and prevent default navigation
        // If target is '_self', allow normal navigation even when embedded
        if (isEmbedded && target === '_blank') {
          event.preventDefault()
          postMessageToParent(href)
        }
        // Otherwise, allow normal navigation (no preventDefault, no postMessage)
      }

      return {
        onClick: handleClick,
        target
      }
    },
    [isEmbedded]
  )

  return createHandler
}
